import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ProRoute from "./components/ProRoute";

// Critical path — loaded immediately (login/register/home)
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import HomePage from "./pages/HomePage";
import Sidebar from "./components/Sidebar";
import { Menu } from "lucide-react";
import Header from "./components/Header";
import CookieConsentBanner from "./components/CookieConsentBanner";
import EmailVerificationBanner from "./components/EmailVerificationBanner";
import { Toaster } from "./components/ui/toaster";

// Lazy-loaded pages — only fetched when navigated to
const LandingPage = lazy(() => import("./pages/LandingPage"));
const CryptoPage = lazy(() => import("./pages/CryptoPage"));
const EquitiesPage = lazy(() => import("./pages/EquitiesPage"));
const ThematicInvestingPage = lazy(() => import("./pages/ThematicInvestingPage"));
const CommoditiesPage = lazy(() => import("./pages/CommoditiesPage"));
const MacroPage = lazy(() => import("./pages/MacroPage"));
const SentimentPage = lazy(() => import("./pages/SentimentPage"));
const WatchlistPage = lazy(() => import("./pages/WatchlistPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const AssetDetailPage = lazy(() => import("./pages/AssetDetailPage"));
const PremiumIntelligencePage = lazy(() => import("./pages/PremiumIntelligencePage"));
const CuriosityPage = lazy(() => import("./pages/CuriosityPage"));
const ScreenerPage = lazy(() => import("./pages/ScreenerPage"));
const CorporatePage = lazy(() => import("./pages/CorporatePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const SubscriptionCancel = lazy(() => import("./pages/SubscriptionCancel"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AIAgentsPage = lazy(() => import("./pages/AIAgentsPage"));
const SingleStocksUniverse = lazy(() => import("./pages/single-stocks/SingleStocksUniverse"));
const SingleStockPage = lazy(() => import("./pages/single-stocks/SingleStockPage"));
const SingleStocksCompare = lazy(() => import("./pages/single-stocks/SingleStocksCompare"));
const SingleStocksAdminPage = lazy(() => import("./pages/SingleStocksAdminPage"));
const ProMaxWall = lazy(() => import("./components/single-stocks/ProMaxWall"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));

// Loading fallback — minimal spinner for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="flex flex-col items-center space-y-3">
      <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-gray-400">Loading...</p>
    </div>
  </div>
);

// Layout wrapper that shows email verification banner for unverified users
function VerificationLayout() {
  const { user } = require('./contexts/AuthContext').useAuth();
  const [emailVerified, setEmailVerified] = React.useState(true);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    if (user?.token) {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      fetch(`${BACKEND_URL}/api/auth/verification-status`, {
        headers: { Authorization: `Bearer ${user.token}` }
      }).then(res => res.json()).then(data => {
        setEmailVerified(data.email_verified !== false);
      }).catch(() => setEmailVerified(true));
    }
  }, [user?.token]);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="md:hidden flex items-center h-12 px-4 border-b border-gray-200 bg-white flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} data-testid="sidebar-hamburger" aria-label="Open menu" className="text-navy p-1">
            <Menu className="w-6 h-6" />
          </button>
        </div>
        {!emailVerified && <EmailVerificationBanner user={user} />}
        <Header />
        <main className="flex-1 overflow-y-auto">
          <div className="container mx-auto px-6 py-8">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Free tier routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/crypto" element={<CryptoPage />} />
                <Route path="/crypto/:symbol" element={<AssetDetailPage assetType="crypto" />} />
                <Route path="/equities" element={<EquitiesPage />} />
                <Route path="/equities/:symbol" element={<AssetDetailPage assetType="equities" />} />
                <Route path="/commodities" element={<CommoditiesPage />} />
                <Route path="/commodities/:symbol" element={<AssetDetailPage assetType="commodities" />} />
                <Route path="/macro" element={<MacroPage />} />
                <Route path="/screener" element={<ScreenerPage />} />
                <Route path="/curiosity" element={<CuriosityPage />} />
                <Route path="/corporate" element={<CorporatePage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/settings" element={<SettingsPage />} />

                {/* PRO tier routes - gated by feature */}
                <Route path="/sentiment" element={<ProRoute feature="sentiment"><SentimentPage /></ProRoute>} />
                <Route path="/watchlist" element={<ProRoute feature="watchlist"><WatchlistPage /></ProRoute>} />
                <Route path="/portfolio" element={<ProRoute feature="watchlist"><WatchlistPage /></ProRoute>} />
                <Route path="/thematic-investing" element={<ProRoute feature="thematic_investing"><ThematicInvestingPage /></ProRoute>} />
                <Route path="/premium-intelligence" element={<ProRoute feature="crypto_intel"><PremiumIntelligencePage /></ProRoute>} />
                {/* News Intel removed */}
                <Route path="/agents" element={<ProRoute feature="ai_agents"><AIAgentsPage /></ProRoute>} />
                <Route path="/single-stocks" element={<ProRoute feature="single_stocks"><SingleStocksUniverse /></ProRoute>} />
                <Route path="/single-stocks/compare" element={<ProRoute feature="single_stocks"><SingleStocksCompare /></ProRoute>} />
                <Route path="/single-stocks/:ticker" element={<ProRoute feature="single_stocks"><SingleStockPage /></ProRoute>} />
                <Route path="/admin/single-stocks" element={<SingleStocksAdminPage />} />
              </Routes>
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes */}
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Protected routes */}
            <Route path="/*" element={
              <ProtectedRoute>
                <SubscriptionProvider>
                  <VerificationLayout />
                </SubscriptionProvider>
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>

        {/* Cookie Consent Banner - GDPR Compliance */}
        <CookieConsentBanner />

        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
